import { FormattedMessage, useIntl } from 'react-intl';
import { Box, ClosableDrawer, PrimaryPaper } from '@atoms/layout';
import { Alert, Chip, Grid, Stack, styled, Tooltip, Typography, useTheme } from '@mui/material';
import { Button } from '@atoms/buttons';
import { useNotification } from '@libs/snackbar';
import { useForm } from 'react-hook-form';
import { Form, FormTextField, FormTimePicker } from '@molecules/form';

import { useShiftTypePresetDrawerStore } from '@libs/store/shiftTypePresets';
import dayjs, { Dayjs } from 'dayjs';
import { getShiftType } from '@libs/models/shifts/shift';
import { useEffect } from 'react';
import { parseError } from '@libs/api/errors';
import { useLocationStore } from '@libs/store/locations';

type DrawerProps = {
  onSaveCall?: () => void;
};
export function EditShiftTypePresetDrawer({ onSaveCall }: DrawerProps) {
  const intl = useIntl();
  const theme = useTheme();
  const { showError, showErrorIntl } = useNotification();
  const {
    open,
    loading: entityLoading,
    currentEntityId,
    entity,
    closeDrawer,
    create,
    update,
    delete: deleteEntity,
  } = useShiftTypePresetDrawerStore();
  const { currentLocationID, loadSimple } = useLocationStore();
  const drawerMode = currentEntityId ? 'edit' : 'create';
  const localizationBaseSubset = 'templates.shiftTypePresets.drawers';
  const localizationSubset = `${localizationBaseSubset}.shiftTypePresetDrawer.${drawerMode}`;

  const loading = entityLoading;

  const methods = useForm<InvitationFormData>({
    defaultValues: {
      name: entity?.name || '',
      startTime: entity?.startTime ? dayjs(entity?.startTime) : null,
      endTime: entity?.startTime ? dayjs(entity?.startTime).add(entity.durationInSeconds, 'seconds') : null,
    },
  });

  useEffect(() => {
    if (!currentLocationID) {
      loadSimple();
    }
  }, [currentLocationID]);

  useEffect(() => {
    if (entity) {
      methods.reset({
        name: entity.name,
        startTime: entity.startTime ? dayjs(entity.startTime, 'HH:mm:ss') : null,
        endTime: entity.startTime ? dayjs(entity.startTime, 'HH:mm:ss').add(entity.durationInSeconds, 'seconds') : null,
      });
    } else {
      methods.reset({
        name: '',
        startTime: null,
        endTime: null,
      });
    }
  }, [entity, methods]);

  const handleClose = () => {
    closeDrawer();
  };

  const handleSubmit = async (data: InvitationFormData) => {
    const dataToSend = {
      name: data.name,
      startTime: data.startTime!.format('HH:mm:ss'),
      durationInSeconds: data.endTime
        ? dayjs(
            data.endTime.add(
              data.startTime!.isAfter(data.endTime) || data.startTime!.isSame(data.endTime) ? 1 : 0,
              'day',
            ),
          ).diff(data.startTime, 'seconds')
        : 0,
    };
    try {
      if (drawerMode === 'create') {
        await create({
          ...dataToSend,
          locationId: currentLocationID,
        });
        return;
      }
      if (drawerMode === 'edit' && currentEntityId) {
        await update({
          ...dataToSend,
          id: currentEntityId,
          locationId: currentLocationID,
        });
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        showErrorIntl(parseError(error, `${localizationBaseSubset}.errors`).message);
      }
      throw error;
    }
  };

  const handleNextPage = async () => {
    try {
      await methods.handleSubmit(handleSubmit)();
      if (Object.keys(methods.formState.errors).length) {
        // stop propagation, while form is invalid
        return;
      }
    } catch (error) {
      console.error('Error while submitting. Stop propagation', error);
      // stop propagation
      return;
    }
    onSaveCall && onSaveCall();
    handleClose();
  };

  const handleDelete = async () => {
    if (currentEntityId) {
      try {
        await deleteEntity(currentEntityId);
        onSaveCall && onSaveCall();
        handleClose();
      } catch (error) {
        if (error instanceof Error) {
          showErrorIntl(parseError(error, `${localizationBaseSubset}.errors.delete`).message);
        }
      }
    }
  };

  const startTime = methods.watch('startTime');
  const endTime = methods.watch('endTime');

  let dialogHeading = `${localizationSubset}.dialogHeading`;

  return (
    <ClosableDrawer
      open={open}
      onClose={handleClose}
      loading={loading}
      controls={
        <Stack direction={'row-reverse'} spacing={2} sx={(theme) => ({ paddingBottom: theme.spacing(1) })}>
          {drawerMode === 'create' && (
            <MenuButton variant="contained" onClick={handleNextPage} disabled={loading || entity?.isReadonly}>
              <FormattedMessage id={`${localizationSubset}.menuButtons.save.label`} />
            </MenuButton>
          )}
          {drawerMode === 'edit' && (
            <>
              <Tooltip title={<FormattedMessage id={`${localizationSubset}.warningMessage.label`} />} arrow>
                <MenuButton variant="contained" onClick={handleNextPage} disabled={loading || entity?.isReadonly}>
                  <FormattedMessage id={`${localizationSubset}.menuButtons.save.label`} />
                </MenuButton>
              </Tooltip>
              <MenuButton
                variant="outlined"
                color="error"
                onClick={handleDelete}
                disabled={loading || entity?.isReadonly}
              >
                <FormattedMessage id={`${localizationSubset}.menuButtons.delete.label`} />
              </MenuButton>
            </>
          )}
          <Box flex={1} />
          <MenuButton
            onClick={handleClose}
            sx={(theme) => ({ color: theme.palette.text.secondary })}
            disabled={loading || entity?.isReadonly}
          >
            <FormattedMessage id={`${localizationSubset}.menuButtons.cancel.label`} />
          </MenuButton>
        </Stack>
      }
    >
      <Stack direction={'column'} sx={{ height: '100%' }} spacing={2} width={{ xs: '300px', sm: '600px' }}>
        {/* Header */}
        <Stack direction={'row'}>
          <Typography variant="h5">
            <FormattedMessage id={dialogHeading} />
          </Typography>
          <Box flex={1} />
        </Stack>
        <Form formUtils={methods} onSubmit={handleSubmit}>
          <Stack direction={'column'} spacing={2}>
            <PrimaryPaper>
              <Grid container spacing={2} sx={{ paddingX: theme.spacing(0.5) }}>
                <Grid item xs={6}>
                  <FormTextField
                    label={<FormattedMessage id={`${localizationSubset}.typeName.label`} />}
                    fullWidth
                    required
                    name="name"
                    placeholder={intl.formatMessage({
                      id: `${localizationSubset}.typeName.placeholder`,
                    })}
                    disabled={loading || entity?.isReadonly}
                    options={{
                      maxLength: 255,
                    }}
                  />
                </Grid>
                <Grid item xs={6}></Grid>
                <Grid item xs={6}>
                  <FormTimePicker
                    disabled={loading || entity?.isReadonly}
                    label={<FormattedMessage id={`${localizationSubset}.startTime.label`} />}
                    fullWidth
                    name="startTime"
                    defaultValue={null}
                    required
                    placeholder={intl.formatMessage({ id: `${localizationSubset}.startTime.placeholder` })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormTimePicker
                    disabled={loading || entity?.isReadonly}
                    label={<FormattedMessage id={`${localizationSubset}.endTime.label`} />}
                    fullWidth
                    name="endTime"
                    defaultValue={null}
                    required
                    placeholder={intl.formatMessage({ id: `${localizationSubset}.endTime.placeholder` })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">
                    <FormattedMessage id={`${localizationSubset}.shiftType.label`} />
                  </Typography>
                  {startTime && (
                    <Chip
                      label={
                        <Stack direction={'row'} spacing={1}>
                          <ShiftTypeIndicator bgcolor={theme.palette.shiftType[getShiftType(startTime)]} />
                          <Typography variant="caption">
                            <FormattedMessage
                              id={`${localizationSubset}.shiftType.variants.${getShiftType(startTime)}`}
                            />
                          </Typography>
                        </Stack>
                      }
                    />
                  )}
                  {!startTime && (
                    <Chip disabled label={<FormattedMessage id={`${localizationSubset}.shiftType.notSelected`} />} />
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">
                    <FormattedMessage id={`${localizationSubset}.duration.label`} />
                  </Typography>
                  {startTime && endTime && (
                    <Chip
                      label={dayjs
                        .duration(
                          dayjs(
                            endTime.add(startTime.isAfter(endTime) || startTime.isSame(endTime) ? 1 : 0, 'day'),
                          ).diff(startTime, 'milliseconds'),
                        )
                        .humanize()}
                    />
                  )}
                  {(!startTime || !endTime) && (
                    <Chip disabled label={<FormattedMessage id={`${localizationSubset}.duration.notSelected`} />} />
                  )}
                </Grid>
              </Grid>
            </PrimaryPaper>
            {drawerMode === 'edit' && (
              <Alert severity="warning">
                <FormattedMessage id={`${localizationSubset}.warningMessage.label`} />
              </Alert>
            )}
          </Stack>
        </Form>
      </Stack>
    </ClosableDrawer>
  );
}

const MenuButton = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(16.75),
}));

const ShiftTypeIndicator = styled(Box)(({ theme }) => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  flexShrink: 0,
  marginTop: `${theme.spacing(0.5)} !important`,
}));

type InvitationFormData = {
  name: string;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
};
